// The sidebar (docs/DESIGN.md, "The shell"): for Student Learning, the learner activity picker
// and the activity's tabs; for School Improvement, its own tabs. A tab appears only once its
// screen exists (docs/DESIGN.md).

import { activityLine, byUnit, type Activity } from "../activities";
import { PRODUCTS, type Product, type Section, type TabSpec } from "../products";
import { Icon } from "./Icon";
import { useMenu } from "./useMenu";

interface Props {
  product: Product;
  tabs: TabSpec[];
  section: Section;
  onSection: (s: Section) => void;
  // Student Learning only. `activities` is null until /api/activities answers.
  activities: Activity[] | null;
  activitiesAvailable: boolean;
  activity: Activity | null;
  onActivity: (a: Activity) => void;
  bare?: boolean;
  activitiesFailed?: boolean;
}

export function Sidebar(p: Props) {
  const renderGroups = (tabs: TabSpec[]) =>
    Object.entries(tabs.reduce<Record<string, TabSpec[]>>(
      (g, t) => ((g[t.group] ||= []).push(t), g), {})
    ).map(([group, inGroup]) => (
      <div className={"nav-group" + (group === "reference" ? " nav-reference" : "")} key={group}>
        {PRODUCTS[p.product].groups[group as TabSpec["group"]] && (
          <h2>{PRODUCTS[p.product].groups[group as TabSpec["group"]]}</h2>
        )}
        {inGroup.map((t) => (
          <button key={t.section} className="nav-link" aria-current={t.section === p.section ? "page" : undefined}
                  onClick={() => p.onSection(t.section)}>
            <span className="l">{t.label}</span>
          </button>
        ))}
      </div>
    ));

  // Split by scope, because the picker only governs half of it. Home is about the CLASS; Student
  // work and Release are about ONE assignment. With the picker at the top it sat above Home and
  // appeared to scope it, which is a promise the screen does not keep — changing the activity
  // changes nothing on a class-scoped page. Below Home, it reads as what it is: the thing that
  // chooses which assignment the tabs under it are about.
  const classTabs = p.tabs.filter((t) => t.scope === "product");
  const assignmentTabs = p.tabs.filter((t) => t.scope === "activity");

  return (
    <nav className="sidebar" id="sidebar" aria-label={PRODUCTS[p.product].label}>
      {renderGroups(classTabs)}
      {p.product === "writing" && !p.bare && assignmentTabs.length > 0 && (
        <ActivityPicker activities={p.activities} available={p.activitiesAvailable}
                        failed={!!p.activitiesFailed} activity={p.activity} onActivity={p.onActivity} />
      )}
      {renderGroups(assignmentTabs)}
    </nav>
  );
}

function ActivityPicker({ activities, available, failed, activity, onActivity }:
    { activities: Activity[] | null; available: boolean; failed: boolean; activity: Activity | null;
      onActivity: (a: Activity) => void }) {
  const m = useMenu("activity");
  if (activities === null) {
    return <div className="picker picker-empty" aria-busy="true"><span className="picker-k">Learner activity</span><span className="picker-m">Loading…</span></div>;
  }
  // Activities exist but none is chosen — after a link to one this teacher can't see. Offer them;
  // saying there are none, beside a page offering to open them, would contradict it.
  if (available && !activity && activities.length > 0) {
    return (
      <div className="picker">
        <button className="picker-btn" aria-haspopup="listbox" {...m.buttonProps}>
          <span className="picker-k">Learner activity <Icon name="updown" /></span>
          <span className="picker-t">Choose an activity</span>
        </button>
        <ActivityList activities={activities} current={null} onChoose={(a) => { m.close(); onActivity(a); }}
                      menuProps={m.menuProps} />
      </div>
    );
  }
  if (!available || !activity) {
    // Not loaded yet, or no activity for any of your classes. Either way the screens below still
    // show your work; the picker says why it has nothing to choose, rather than disappearing.
    return (
      <div className="picker picker-empty">
        <span className="picker-k">Learner activity</span>
        <span className="picker-m">
          {failed ? "Activities couldn't load. Your cohorts' work still shows here."
            : available ? "No activities for your cohorts yet." : "Activities haven't loaded yet."}
        </span>
      </div>
    );
  }
  return (
    <div className="picker">
      <button className="picker-btn" aria-haspopup="listbox" {...m.buttonProps}>
        <span className="picker-k">Learner activity <Icon name="updown" /></span>
        <span className="picker-t">{activity.lesson_title}</span>
        <span className="picker-m">{activityLine(activity)}</span>
      </button>
      <ActivityList activities={activities} current={activity}
                    onChoose={(a) => { m.close(); if (a.activity_id !== activity.activity_id) onActivity(a); }}
                    menuProps={m.menuProps} />
    </div>
  );
}

function ActivityList({ activities, current, onChoose, menuProps }:
    { activities: Activity[]; current: Activity | null; onChoose: (a: Activity) => void;
      menuProps: ReturnType<typeof useMenu>["menuProps"] }) {
  return (
    <div className="menu" role="listbox" aria-label="Learner activities" {...menuProps}>
      {byUnit(activities).map((g) => (
        <div key={g.unit} role="group" aria-label={g.unit}>
          <div className="picker-group" aria-hidden="true">{g.unit}</div>
          {g.activities.map((a) => (
            <button key={a.activity_id} role="option" aria-selected={a.activity_id === current?.activity_id}
                    className="menu-item act-line" onClick={() => onChoose(a)}>
              <span className="menu-item-body">
                <b>{a.lesson_title}</b>
                <small>{activityLine(a)}{a.due ? ` · due ${shortDate(a.due)}` : ""}</small>
              </span>
              {!a.scored && <span className="tag">Not scored</span>}
            </button>
          ))}
        </div>
      ))}
      <div className="menu-note menu-foot">Each line is a lesson, taught to one section, in one iteration.</div>
    </div>
  );
}

function shortDate(iso: string): string {
  const d = new Date(iso.length === 10 ? `${iso}T12:00` : iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
